/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { isEqual, keyBy } from 'lodash';
import styled from 'styled-components';
import { Button, Box, Form } from 'grommet';

import { getRecipients, patchRecipients } from 'blb-api/tasks';
import { Modal } from 'blb-components/GrommetComponents';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { StyledEditRecipientRow } from './styles';
import { EditRecipientRow } from './EditRecipientRow';
import { AlertBar } from 'blb-components/GrommetComponents/AlertBar';

const MODAL_DIMENSION = '720px';

export namespace EditRecipientsModal {
  export interface Props {
    envelopeProviderId: string;
    handleClose: () => void;
    isOpen: boolean;
    providerUrl: string;
    taskId: string;
  }
}

export const ModalContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
`;

interface RecipientUpdateState {
  isValid: boolean;
  update: BlendBridgeInternal.API.PatchRecipientOverride;
}

export const EditRecipientsModal: React.FC<EditRecipientsModal.Props> = ({
  taskId,
  envelopeProviderId,
  isOpen,
  providerUrl,
  handleClose,
}) => {
  const [recipients, setRecipients] = useState<BlendBridgeInternal.API.EsignRecipient[]>([]);
  const [recipientUpdates, setRecipientUpdates] = useState<RecipientUpdateState[]>([]);
  const [requestError, setRequestError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getEsignRecipients = async () => {
    setIsLoading(true);
    try {
      const { data: recipientList } = await getRecipients(taskId, envelopeProviderId);
      setRecipients(recipientList);
    } catch (error) {
      setRequestError(`Error retrieving recipients, please refresh and try again: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Get the currently saved recipients whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getEsignRecipients();
    }
  }, [isOpen]);

  const createChangeHandler = (partyId: string) => (
    update: BlendBridgeInternal.API.PatchRecipientOverride,
    isValid: boolean,
  ) => {
    const { email, ...original } = recipients.find(recipient => recipient.partyId === partyId)!;
    const newRecipientUpdates = keyBy(recipientUpdates, 'update.partyId');
    if (isEqual(update, original)) {
      delete newRecipientUpdates[partyId];
    } else {
      newRecipientUpdates[partyId] = { isValid, update };
    }
    setRecipientUpdates(Object.values(newRecipientUpdates));
  };

  const reloadIFrame = (): void => {
    const iframeComponent = document.querySelector('iframe');
    if (iframeComponent) {
      iframeComponent.src = providerUrl;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const recipientUpdatesList = recipientUpdates.map(({ update }) => update);
      await patchRecipients(taskId, envelopeProviderId, recipientUpdatesList);
      reloadIFrame();
      handleClose();
    } catch (error) {
      setRequestError(`Error updating recipients: ${(error as Error).message}`);
    }
  };

  const isFormValid = useMemo(() => recipientUpdates.length > 0 && recipientUpdates.every(({ isValid }) => isValid), [
    recipientUpdates,
  ]);
  return (
    <Modal
      ariaHideApp={false}
      buttonComponents={
        <Button
          disabled={isSubmitting || !isFormValid}
          id={`it-task-${taskId}-EditRecipientModal-SaveEditsBtn`}
          label="Save edits"
          onClick={handleSubmit}
          primary
          type="submit"
        />
      }
      contentBoxProps={{
        height: {
          height: 'fit-content',
          max: MODAL_DIMENSION,
        },
        overflow: 'hidden',
        pad: 'medium',
      }}
      handleClose={handleClose}
      id={`it-task-${taskId}-EditRecipientModal`}
      isOpen={isOpen}
      style={{
        content: {
          height: 'fit-content',
          width: MODAL_DIMENSION,
        },
        overlay: {
          // Required until Alchemy 3.x is fixed for modals
          zIndex: 100,
        },
      }}
      title="Edit recipients"
    >
      {!!requestError ? <AlertBar errorMessage={requestError} id="edit-recipient-error" /> : null}
      {isLoading ? (
        <CenteredLoadingSpinner />
      ) : (
        <Form onSubmit={handleSubmit} validate="change">
          <StyledEditRecipientRow flex>
            {Object.values(recipients).map(recipient => (
              <EditRecipientRow
                key={recipient.partyId}
                onChange={createChangeHandler(recipient.partyId)}
                recipient={recipient}
              />
            ))}
          </StyledEditRecipientRow>
        </Form>
      )}
    </Modal>
  );
};
